import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { userService } from '../../../_services';
import * as z from 'zod';

const addUserBaseSchema = z.object({
    Fname: z.string().min(3, 'First Name must be at least 3 characters long'),
    Lname: z.string().min(2, 'Last Name must be at least 2 characters long'),
    Phone: z.string()
        .min(1, 'Phone Number is required')
        .refine(val => val.replace(/[- )(]/g, '').length === 10, 'Phone Number must be 10 digits'),
    Email: z.string()
        .min(1, 'Email is required')
        .refine(val => userService.ValidEmailRegex(val), 'Invalid Email'),
});

const addUserWithPassSchema = addUserBaseSchema.extend({
    Username: z.string()
        .refine(val => val.length >= 5, 'Username must be at least 5 characters long')
        .refine(val => !val.includes(' '), 'Username does not allow space'),
    Password: z.string().min(6, 'Password must be at least 6 characters long'),
});

function AddUserDialog({ open, onOpenChange, onSuccess }) {
    const [formData, setFormData] = useState({
        Fname: '',
        Lname: '',
        Phone: '',
        Email: '',
        isuserpass: false,
        Username: '',
        Password: ''
    });
    
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [submitMessage, setSubmitMessage] = useState({ type: '', text: '' });

    const handleInputChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
        setSubmitMessage({ type: '', text: '' });
    };

    const handlePhoneChange = (value) => {
        // Allow digits, spaces, dashes, and parentheses while typing
        const cleaned = value.replace(/[^\d\s\-()+]/g, '');
        handleInputChange('Phone', cleaned);
    };

    const handleNameChange = (name, value) => {
        const nameRegex = /^[a-zA-Z]*$/;
        if (value === '' || nameRegex.test(value)) {
            handleInputChange(name, value);
        }
    };

    const validate = async () => {
        const schema = formData.isuserpass ? addUserWithPassSchema : addUserBaseSchema;
        const result = schema.safeParse(formData);
        const fe = result.success ? {} : result.error.flatten().fieldErrors;
        const newErrors = {
            ...(fe.Fname?.[0] && { Fname: fe.Fname[0] }),
            ...(fe.Lname?.[0] && { Lname: fe.Lname[0] }),
            ...(fe.Phone?.[0] && { Phone: fe.Phone[0] }),
            ...(fe.Email?.[0] && { Email: fe.Email[0] }),
            ...(fe.Username?.[0] && { Username: fe.Username[0] }),
            ...(fe.Password?.[0] && { Password: fe.Password[0] }),
        };

        // Async phone API check � only if phone passes sync validation
        if (!newErrors.Phone && formData.Phone) {
            try {
                const phoneValidation = await userService.GetPhoneNoValidate(formData.Phone);
                if (phoneValidation && phoneValidation[0]?.code !== "1") {
                    newErrors.Phone = 'Invalid Phone Number';
                }
            } catch (error) {
                newErrors.Phone = 'Error validating phone number';
            }
        }

        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        setLoading(true);
        setSubmitMessage({ type: '', text: '' });

        try {
            const newErrors = await validate();
            
            if (Object.keys(newErrors).length > 0) {
                setErrors(newErrors);
                setLoading(false);
                return;
            }

            let apidata;

            if (formData.isuserpass) {
                // Switch ON → create user with username/password immediately
                apidata = await userService.CreateNewUserByAdmin(
                    formData.Fname,
                    formData.Lname,
                    formData.Phone,
                    formData.Email,
                    formData.Username,
                    formData.Password
                );
            } else {
                // Switch OFF → send email invite (no username/password)
                const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
                const [UserId, UserName] = Usermeta_Data.split(':');

                apidata = await userService.AddNewUser(
                    UserId,
                    UserName,
                    formData.Fname,
                    formData.Lname,
                    formData.Phone,
                    formData.Email,
                    '',
                    ''
                );
            }

            if (apidata && apidata.length > 0) {
                if (apidata[0].code === "1") {
                    setSubmitMessage({ type: 'success', text: apidata[0].message || 'User added successfully!' });
                    if (onSuccess) onSuccess();
                    setTimeout(() => {
                        handleClose();
                    }, 1500);
                } else {
                    setSubmitMessage({ type: 'error', text: apidata[0].message || 'Error adding user' });
                }
            } else {
                setSubmitMessage({ type: 'error', text: 'Error adding user' });
            }
        } catch (error) {
            console.error('Error adding user:', error);
            setSubmitMessage({ type: 'error', text: 'Error adding user' });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            Fname: '',
            Lname: '',
            Phone: '',
            Email: '',
            isuserpass: false,
            Username: '',
            Password: ''
        });
        setErrors({});
        setSubmitMessage({ type: '', text: '' });
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle className="text-xl font-semibold text-primary">
                        Add New User
                    </DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto pr-2">
                    <div className="grid grid-cols-2 gap-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="Fname">First Name *</Label>
                            <Input
                                id="Fname"
                                value={formData.Fname}
                                onChange={(e) => handleNameChange('Fname', e.target.value)}
                                className={errors.Fname ? 'border-destructive' : ''}
                                placeholder="Enter first name"
                            />
                            {errors.Fname && <p className="text-sm text-destructive">{errors.Fname}</p>}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="Lname">Last Name *</Label>
                            <Input
                                id="Lname"
                                value={formData.Lname}
                                onChange={(e) => handleNameChange('Lname', e.target.value)}
                                className={errors.Lname ? 'border-destructive' : ''}
                                placeholder="Enter last name"
                            />
                            {errors.Lname && <p className="text-sm text-destructive">{errors.Lname}</p>}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="Phone">Phone Number *</Label>
                            <Input
                                id="Phone"
                                value={formData.Phone}
                                onChange={(e) => handlePhoneChange(e.target.value)}
                                className={errors.Phone ? 'border-destructive' : ''}
                                placeholder="(555) 555-5555"
                            />
                            {errors.Phone && <p className="text-sm text-destructive">{errors.Phone}</p>}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="Email">Email *</Label>
                            <Input
                                id="Email"
                                type="email"
                                value={formData.Email}
                                onChange={(e) => handleInputChange('Email', e.target.value)}
                                className={errors.Email ? 'border-destructive' : ''}
                                placeholder="user@example.com"
                            />
                            {errors.Email && <p className="text-sm text-destructive">{errors.Email}</p>}
                        </div>

                        <div className="space-y-2 col-span-2">
                            <div className="flex items-center space-x-2">
                                <Switch
                                    id="isuserpass"
                                    checked={formData.isuserpass}
                                    onCheckedChange={(checked) => handleInputChange('isuserpass', checked)}
                                />
                                <Label htmlFor="isuserpass" className="cursor-pointer">
                                    Create Username and Password
                                </Label>
                            </div>
                        </div>

                        {formData.isuserpass && (
                            <>
                                <div className="space-y-2">
                                    <Label htmlFor="Username">Username *</Label>
                                    <Input
                                        id="Username"
                                        value={formData.Username}
                                        onChange={(e) => handleInputChange('Username', e.target.value)}
                                        className={errors.Username ? 'border-destructive' : ''}
                                        placeholder="Enter username (min 5 characters)"
                                    />
                                    {errors.Username && <p className="text-sm text-destructive">{errors.Username}</p>}
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="Password">Password *</Label>
                                    <Input
                                        id="Password"
                                        type="password"
                                        value={formData.Password}
                                        onChange={(e) => handleInputChange('Password', e.target.value)}
                                        className={errors.Password ? 'border-destructive' : ''}
                                        placeholder="Enter password (min 6 characters)"
                                    />
                                    {errors.Password && <p className="text-sm text-destructive">{errors.Password}</p>}
                                </div>
                            </>
                        )}
                    </div>

                    {submitMessage.text && (
                        <Alert 
                            variant={submitMessage.type === 'success' ? 'success' : 'destructive'} 
                            className="mt-4"
                        >
                            {submitMessage.type === 'success' ? (
                                <CheckCircle className="h-4 w-4" />
                            ) : (
                                <AlertCircle className="h-4 w-4" />
                            )}
                            <AlertDescription>{submitMessage.text}</AlertDescription>
                        </Alert>
                    )}

                    <DialogFooter className="mt-6">
                        <Button
                            type="button"
                            variant="default"
                            onClick={handleClose}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="secondary"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                    Adding User...
                                </>
                            ) : (
                                'Add User'
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export default AddUserDialog;

